import { useChartExport } from '@ecosystem/export-utils';
import { ExportFormat, FilterToggleButton, useAnalytics } from '@ecosystem/newron-client-lib';
import { containers, layouts } from '@ecosystem/newron-design-system';
import { SortingOptions } from '@components/design/migrated/Table/Sorting/types';
import axios from 'axios';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { batch, useDispatch, useSelector } from 'react-redux';
import { bindActionCreators } from 'redux';
import CategoryLeaderboardCopyViewButton from '@components/category-leaderboard-copy-button/CategoryLeaderboardCopyViewButton';
import CategoryGroup from '@components/category-leaderboard/CategoryGroup';
import { CategoryLeaderboardActionBar } from '@components/category-leaderboard/category-leaderboard-group/CategoryLeaderboardActionBar';
import { CreateCategoryOrSegmentsLeaderboardModalGroup } from '@components/category-leaderboard/category-leaderboard-group/create-group/CreateCategoryOrSegmentsLeaderboardModalGroup';
import { DeleteCategoryOrSegmentsLeaderboardModalGroup } from '@components/category-leaderboard/category-leaderboard-group/delete-group/DeleteCategoryOrSegmentsLeaderboardModalGroup';
import CategoryLeaderboardHeaderIcon from '@components/category-leaderboard/header-icon/CategoryLeaderboardHeaderIcon';
import CategoryOrSegmentsLeaderboardGroupProvider from '@components/category-leaderboard/utils/CategoryOrSegmentsLeaderboardGroupProvider';
import ComparisonGrowthSelector from '@components/comparison-growth-selector/ComparisonGrowthSelector';
import { GrowthTypes } from '@components/comparison-growth-selector/Types';
import {
  RelativePeriod,
  RelativePeriodLabel,
  RelativePeriodMapping,
} from '@components/comparison-period-selector/Types';
import WarningProductGroupFilterContainer from '@components/containers/warning-pg-filter/WarningProductGroupFilterContainer';
import {
  displayExportErrorToastMessage,
  getAppliedFiltersText,
  getKpiDisplayNameFromHeaderKey,
} from '@hooks/export/ExportUtils';
import { useExportTable } from '@hooks/export/export-table/UseExportTable';
import { getExportTrackingFormat } from '@hooks/export/export-tracking/ExportTracking';
import FilterChips from '@components/filter-chips/FilterChips';
import FilterSelectionNote from '@components/filter-selection-note/FilterSelectionNote';
import getComparisonPeriod from '@components/filter-selection-note/GetComparisonPeriod';
import InfoModal from '@components/info-modal/InfoModal';
import LoadingOverlay from '@components/loading-overlay/LoadingOverlay';
import MarketPerformancePosFacts from '@components/market-performance-pos-facts/MarketPerformancePosFacts';
import {
  constructCountryOption,
  getRetailerDefaultCountryCode,
  getSelectedClientNameWithClientId,
  getSubscriptionForOtherProducts,
  mapDefaultSubscribedCountry,
  mapSubscribedCountries,
  onChangeHandlerForOtherProducts,
  OnRetailerChangeParam,
  retainTimeFrame,
  saveErrorLogForOnChange,
} from '@components/multi-banner-retailer-selector/MultiBannerRetailerSelectorUtils';
import PageHeader from '@components/page-header/PageHeader';
import PerformanceSegments from '@components/performance-segments/PerformanceSegments';
import TimeFramePickerWrapper from '@components/time-frame-picker/components/wrapper/TimeFramePickerWrapper';
import { applyTimeFramePicker } from '@components/time-frame-picker/utils/TimeFramePickerUtils';
import { Periodicity } from '@components/time-period-selector/PeriodsTypes';
import { useToastMessage } from '@components/toast-message/ToastMessage';
import {
  CategoryLeaderboardGroupingPgKpi,
  CategoryLeaderboardParam,
  CategoryLeaderboardPg,
  CategoryLeaderboardPgPage,
  ColumnHeaderDisplayNameForCL,
  ColumnHeaderForCL,
  DefaultSortingOptionsForCategoryLeaderboard,
  PageRequest,
} from '@models/CategoryLeaderboard';
import { ClientSubscription } from '@models/Opa';
import { RetailerClient } from '@models/RetailerClient';
import { SalesPerformance } from '@models/SalesPerformance';
import { CategoryLeaderboardViewState } from '@models/SavedViews';
import { getEffectiveProductType } from '@models/SubscribedProductType';
import { getCategoryLeaderboard } from '@services/CategoryLeaderboard.service';
import { BaseTimePeriod, MainFilters, TimePeriod } from '@services/FiltersTypes';
import { getProductGroupsByCountry, getProductGroupsMasterByCountry } from '@services/ProductGroup.service';
import { getAbortController } from '@services/utils/OverviewServiceUtils';
import {
  actionCreators,
  actionCreatorsHitlistsRetailerClient,
  actionCreatorsInitialFilters,
  actionCreatorsInitialFiltersMarketPremium,
  actionCreatorsInitialFiltersProducts,
  actionCreatorsMarketPremium,
  actionCreatorsMarketPremiumClientState,
  actionCreatorsProducts,
  actionCreatorsRetailerClient,
  actionCreatorsRO,
  actionCreatorsSideFilters,
} from '../../state/Index';
import { IRootState } from '@state/reducers/Reducers';
import { constructRetailerClientState } from '@state/reducers/retailer-client/RetailerClientUtils';
import { getAndStoreSavedFilters } from '@utils/AppSubscriptionUtils';
import { filterPGsByCountry, getCountryFilterPairsByTimeFrame, updateCountryAndPGFilters } from '@utils/CountryUtils';
import { retailerOverviewResponseConfig } from '@utils/FilterSettings';
import {
  checkPGsByCountryPrecondition,
  constructCountryOptionForSideMenu,
  getFeatureValueForRetailer,
  getFiltersForClient,
  getFiltersInitialState,
  getInitialAppliedCountries,
  getProductGroupFromSideFilters,
  getSubscriptionPeriodicitiesByCountryAndProductType,
  removeUnsavedFilters,
  RetailerDetails,
  updateFieldsForOverviewAndCategoryLeaderboardAndExplore,
  updatePgsByCountryOnCountryApplyWithProductType,
  validateStoreFilterSettings,
} from '@utils/FilterUtils';
import { ErrorTagEnum, saveErrorLog } from '@utils/LogUtil';
import {
  checkPeriodExist,
  getAndUpdatePeriod,
  getPeriodicityForMATAndYTD,
  getPeriodMeta,
  isBaseTimePeriodFormat,
} from '@utils/PeriodUtils';
import { isAuthorized } from '@utils/Permission';
import { getFactRevenue } from '@utils/currencyUtils/CurrencyUtils';
import { getVolumeBasedOnPeriodType } from '@utils/factVolumeUtils/FactVolumeUtils';
import { useFetchSalesPerformance } from '@utils/hook/marketPerformance/UseFetchSalesPerformance';
import { getFeatureSelector } from '@utils/hook/selector/ShowPerformanceWithinSelector';
import { useSideMenuFilters } from '@utils/hook/sideFilters/SideFiltersHooks';
import TableBoardFilter from '@utils/hook/tableFilter/TableBoardFilter';
import { useCategoryLeaderboardGroupState } from '@utils/hook/userPreferences/UseGroupState';
import { ElementType, TrackingGeneralProp } from '@utils/page-analytics/model/AnalyticTrackingBasePropertiesObj';
import {
  dataExportedTrackingFunction,
  DataExportTrackingElementParam,
  useDataExportTracking,
} from '@utils/page-analytics/model/DataExportTracking';
import {
  PAGE_ELEMENT_CLICK_TRACKING_TITLE,
  pageElementClickTrackingFunction,
  PageElementClickTrackingParam,
  TOGGLE_CLICKED_TITLE,
} from '@utils/page-analytics/model/PageElementClickedTracking';
import {
  PAGE_VIEW_TRACKING_TITLE,
  pageViewTrackingFunction,
  PageViewTrackingFunctionParam,
} from '@utils/page-analytics/model/PageViewedTracking';
import { TableName } from '@utils/page-analytics/model/TableClickTracking';
import { usePageConfig } from '@utils/pageConfig/PagesConfigContext';
import {
  Country,
  CountryFilterPair,
  DataType,
  FactRevenue,
  FilterKeys,
  FilterTitle,
  getPageTypeWithPageName,
  Option,
  PageName,
  PageType,
  PageTypeAndName,
  ShareType,
} from '@utils/pageConfig/Types';
import { CATEGORY_CARD_TITLE } from '../Utilities';
import PageInfo from '../info/PageInfo';
import { ProductDataType, ProductType } from '../product-analyzer/Product';
import { Unauthorized } from '../unauthorized/Unauthorized';
import * as styles from './CategoryLeaderboard.module.scss';
import { GroupContext } from '@utils/userPreferencesUtils/Types';
import { MainFiltersState } from '@state/reducers/main-filter/MainFiltersReducer';
import { useUpdatePerfPeriodSet } from '@utils/period-set/UseUpdatePerfPeriodSet';
import { byCountry } from '@state/reducers/initial-filter/InitialFiltersPredicates';
import { useInitializedPeriodSet } from '@utils/period-set/UseInitializedPeriodSet';
import { getFallbackPeriodCombo } from '@utils/period-set/PeriodSetFallback';
import { MainFilterStateRO } from '@state/reducers/main-filter-retailer-overview/MainFiltersRoReducer';
import { MainFilterStateProducts } from '@state/reducers/main-filter-products/MainFiltersProductsReducer';
import { categoryLeaderboardConfiguration } from '../Configuration';
import PgAvailabilityBanner from '../../components/pg-availability-banner/PgAvailabilityBanner';
import {
  ExportDoubleBarChartOptions,
  useExportDoubleBarChart,
} from '@hooks/export/export-double-bar-chart/hooks/UseExportDoubleBarChart';
import { useExportContext } from '@hooks/export/context/ExportContext';
import { CategoryLeaderboardDoubleBarChartInsight } from '@components/collections/category-leaderboard-double-bar-chart/Model';
import { CreateInsightDataFn } from '@ecosystem/newron-client-collections';
import {
  getCategoryLeaderboardColumns,
  getKPIs,
  updateShareTypeAndSortingKeyCL,
} from '@components/category-leaderboard/category-leaderboard-table-columns/CategoryLeaderboardTableColumns';
import {
  PgAvailabilityProps,
  useDispatchPgsAvailability,
} from '@utils/hook/incrementalLoading/UseDispatchPgsAvailability';
import { CollectionDataType } from '@ecosystem/newron-client-collections/build/shared/types';
import { filterByAccessibleProductGroup } from '@utils/collectionUtils/CollectionUtils';
import { getGrandTotalValue, modifiedCLSegmentsGrandTotalToTotalValue } from '@utils/TotalUtils';
import { useUserPersistedFilters } from '@utils/hook/saveFilter/UseUserPersistedFilters';
import { cloneDeep } from 'lodash';
import { ViewType } from '@hooks/export/Model';
import { setApiErrorState } from '@services/utils/ErrorHandlers';

interface Props {
  retailerClients: RetailerClient[];
}

const supportedPeriodicities: Periodicity[] = categoryLeaderboardConfiguration.supportedPeriodicities;

const CategoryLeaderboard: React.FC<Props> = ({ retailerClients }) => {
  const pagesConfig = usePageConfig();
  const { refetch } = useUserPersistedFilters();
  const { Page } = layouts;
  const dispatch = useDispatch();
  const { track } = useAnalytics();

  const { updateMainFiltersRO } = bindActionCreators(actionCreatorsRO, dispatch);
  const { updateMainFilters } = bindActionCreators(actionCreators, dispatch);
  const { updateRetailerClient } = bindActionCreators(actionCreatorsRetailerClient, dispatch);
  const { updateMarketPremiumRetailerClient } = bindActionCreators(actionCreatorsMarketPremiumClientState, dispatch);
  const { updateMainFiltersMarketPremium } = bindActionCreators(actionCreatorsMarketPremium, dispatch);
  const { updateHitlistsRetailerClient } = bindActionCreators(actionCreatorsHitlistsRetailerClient, dispatch);
  const { updateSideFilters } = bindActionCreators(actionCreatorsSideFilters, dispatch);
  const { updateInitialFilters } = bindActionCreators(actionCreatorsInitialFilters, dispatch);
  const { updateInitialFiltersProducts } = bindActionCreators(actionCreatorsInitialFiltersProducts, dispatch);
  const { updateInitialFiltersMarketPremium } = bindActionCreators(actionCreatorsInitialFiltersMarketPremium, dispatch);
  const { updateMainFiltersProducts } = bindActionCreators(actionCreatorsProducts, dispatch);
  const subscribedProductTypes = useSelector(
    (state: IRootState) => state.subscribedProductTypes.subscribedProductTypes,
  );
  const mainFiltersStateRO: MainFilterStateRO = useSelector((state: IRootState) => state.mainFiltersRO);
  const retailerClientState = useSelector((state: IRootState) => state.retailerClient);
  const initialFiltersState = useSelector((state: IRootState) => state.initialFilters);
  const mainFiltersState: MainFiltersState = useSelector((state: IRootState) => state.mainFilters);
  const sideFiltersState = useSelector((state: IRootState) => state.sideFilters);
  const mainFilterStateProducts: MainFilterStateProducts = useSelector(
    (state: IRootState) => state.mainFiltersProducts,
  );
  const initialFiltersProductsState = useSelector((state: IRootState) => state.initialFiltersProducts);
  const hitlistsRetailerClientState = useSelector((state: IRootState) => state.hitlistsRetailerClient);

  const { showToastMessage } = useToastMessage();

  /*
   * Create a default filter to store all the initial filters that was passed from App component initially,
   * so that it can be reused in the page when change to other retailer. Please avoid putting this state in the useEffect.
   */
  const [defaultFilters, setDefaultFilters] = useState<MainFilters[]>(initialFiltersState.initialFilters);

  const [filters, setFilters] = useState<MainFilters>(defaultFilters[0]);

  const defaultPageNumber = 0;
  const defaultPageSize = 20;

  const effectiveProductType = getEffectiveProductType(subscribedProductTypes, PageName.CATEGORY_LEADERBOARD);

  const [selectedGrowthType, setGrowthOption] = useState<GrowthTypes>(mainFiltersStateRO.selectedGrowthType);
  const [selectedShareType, setSelectedShareType] = useState<ShareType>(ShareType.MarketShare);
  const [revenuePerformance, setRevenuePerformance] = useState(new SalesPerformance());
  const [volumePerformance, setVolumePerformance] = useState(new SalesPerformance());

  const [categoryLeaderboardPg, setCategoryLeaderboardPg] = useState<CategoryLeaderboardPg[]>([]);
  const [isApiError, setIsApiError] = useState<boolean>(false);

  const [categoryLeaderboardGroups, setCategoryLeaderboardGroups] = useState<CategoryLeaderboardGroupingPgKpi[]>([]);
  const [loadingCategoryLeaderboard, setLoadingCategoryLeaderboard] = useState<boolean>(true);
  const [pgsByAppliedCountries, setPgsByAppliedCountries] = useState<CountryFilterPair[]>([]);
  const [currentSortingOption, setCurrentSortingOption] = useState<SortingOptions>(
    DefaultSortingOptionsForCategoryLeaderboard,
  );

  const [countryChanged, setCountryChanged] = useState<boolean>(false);
  const isAuthorizedRIUser = isAuthorized(pagesConfig.clientSubscription);
  const isAuthorizedRetailKadUser = isAuthorized(pagesConfig.retailKadClientSubscription);
  const isAuthorizedUser = isAuthorizedRIUser || isAuthorizedRetailKadUser;
  const [isFetchingTableApi, setIsFetchingTableApi] = useState<boolean>(false);
  const [isFetchingCardApi, setIsFetchingCardApi] = useState<boolean>(false);

  useEffect(() => {
    const pageTypeAndName: PageTypeAndName = {
      pageType: PageType.CATEGORY_LEADERBOARD,
      pageName: PageName.CATEGORY_LEADERBOARD,
    };

    //Start Page View Amplitude Tracking
    //Update page viewing
    updateRetailerClient({ ...retailerClientState, pageViewing: PageName.CATEGORY_LEADERBOARD });
    const trackingParam: PageViewTrackingFunctionParam = {
      eventTitle: PAGE_VIEW_TRACKING_TITLE,
      sideFilters: sideFiltersState.appliedFilters,
      retailerClientState: retailerClientState,
      track: track,
      pageType: PageType.CATEGORY_LEADERBOARD, // Won't get from redux because of async. RO & CL page sharing the same pageType in pagesConfig
      pageName: PageName.CATEGORY_LEADERBOARD, // Won't get from redux because of async
      access: pagesConfig.access,
      initialLocationId: getInitialAppliedCountries(pagesConfig, PageType.CATEGORY_LEADERBOARD, effectiveProductType),
    };

    pageViewTrackingFunction(trackingParam);
    validateStoreFilterSettings(sideFiltersState, pagesConfig, pageTypeAndName);
    setGrowthOption(mainFiltersState.selectedGrowthType);
    pagesConfig.setIsNavigateFromRS(true);
    pagesConfig.setIsHidePGInfoWarning(false);
  }, []);

  const allFilters = [...pagesConfig.appliedFilters[PageType.CATEGORY_LEADERBOARD].selectedFilters];
  const pgInitialState = getFiltersInitialState(allFilters, FilterKeys.PRODUCT_GROUP);
  const [currentPage, setCurrentPage] = useState<number>(defaultPageNumber);
  const [totalPage, setTotalPage] = useState<number>(1);
  const [categoryLeaderboardParam, setCategoryLeaderboardParam] = useState<CategoryLeaderboardParam>();
  const [categoryLeaderboardTotalSize, setCategoryLeaderboardTotalSize] = useState<number>(0); //This state is to store the total size of Leaderboard in order to get all the data while exporting Table/Bar chart

  const [selectedProductGroups, setSelectedProductGroups] = useState<Option[] | undefined>(
    pgInitialState !== undefined ? pgInitialState.options : undefined,
  );

  const [appliedProductGroups, setAppliedProductGroups] = useState<Option[]>(
    pgInitialState !== undefined ? pgInitialState.options : [],
  );

  const [appliedCountries, setAppliedCountries] = useState<Option[]>(
    getInitialAppliedCountries(pagesConfig, PageType.CATEGORY_LEADERBOARD, effectiveProductType),
  );

  const [selectedCountries, setSelectedCountries] = useState<Option[]>(
    getInitialAppliedCountries(pagesConfig, PageType.CATEGORY_LEADERBOARD, effectiveProductType),
  );

  //region variables and setters related to period set
  const { updatePeriod, updatePeriodicity, updateComparisonPeriod, updateCustomMonthsPeriodState } =
    useUpdatePerfPeriodSet();
  const timePeriodOptions: TimePeriod = initialFiltersState.initialFilters.filter(
    byCountry(appliedCountries[0].value),
  )[0].timePeriod;
  const { period, periodicity, comparisonPeriod, customMonthsPeriodState } = useInitializedPeriodSet(
    timePeriodOptions,
    retailerClientState.clientId,
  );
  //endregion

  const [loadingPosFacts, setLoadingPosFacts] = useState<boolean>(true);

  const [loadMoreTable, setLoadMoreTable] = useState<boolean>(false);

  const [loadingOverlay, setLoadingOverlay] = useState<boolean>(false);

  const [onPageFirstLoad, setOnPageFirstLoad] = useState<boolean>(true);

  const [totalNumberProductGroups, setTotalNumberProductGroups] = useState<number>(0);

  const [isEditable, setIsEditable] = useState<boolean>(false);

  const [groupingEffect, setGroupingEffect] = useState<boolean>(true);

  const appliedCountry = (): Country | undefined => {
    return pagesConfig.clientCountries.filter((x) => appliedCountries.some((y) => x.id === y.value)).pop();
  };

  const context: GroupContext = useMemo(() => {
    return {
      client: retailerClientState.clientName,
      country: getRetailerDefaultCountryCode(retailerClientState, appliedCountries),
    };
  }, [retailerClientState, appliedCountries]);

  const { allGroupsForApiCalls, getCategoryLeaderboardGroups, getNewSortValues } =
    useCategoryLeaderboardGroupState(context);

  const CURRENT_TOTAL_NAME = getGrandTotalValue();

  //Props for custom hooks to dispatch pgs availability details
  const pgsAvailabilityProps: PgAvailabilityProps = useMemo(
    () => ({
      periodValue: period.value,
      periodicity,
      appliedCountries,
      pgsByAppliedCountry: pagesConfig.pgsByAppliedCountry,
      countriesPgsAvailabilities: pagesConfig.masterPgsByCountry,
      appliedProductGroups,
    }),
    [
      period.value,
      periodicity,
      appliedCountries,
      pagesConfig.pgsByAppliedCountry,
      pagesConfig.masterPgsByCountry,
      appliedProductGroups,
    ],
  );

  useDispatchPgsAvailability(pgsAvailabilityProps);

  const checkValidComparisonPeriod = () => {
    const periodMeta = getPeriodMeta(period.value, comparisonPeriod, periodicity, period.label)!;

    return !(
      periodMeta.comparisonPeriod.toLowerCase().includes('-nan') ||
      periodMeta.comparisonPeriod.toLowerCase().includes('week -1') ||
      periodMeta.comparisonPeriod.toLowerCase().includes('undefined')
    );
  };

  useEffect(() => {
    pagesConfig.setClearFilters(false);
    pagesConfig.setSelectedTimePeriod(period);

    setOnPageFirstLoad(false);
  }, []);

  /*
   * updates selectedProductGroup and appliedCountries when applied filters are changed
   */
  useEffect(() => {
    // Changed to retrieve the state from redux store instead of context
    const appliedFilters = sideFiltersState.appliedFilters[PageType.CATEGORY_LEADERBOARD].selectedFilters;

    setCategoryLeaderboardPg([]);
    setTotalPage(1);
    setCurrentPage(defaultPageNumber);

    if (appliedFilters && appliedFilters.length !== 0) {
      const index = appliedFilters.findIndex((e: any) => e.filterKey === FilterKeys.PRODUCT_GROUP);
      index !== -1 ? setAppliedProductGroups(cloneDeep(appliedFilters[index].options)) : setAppliedProductGroups([]);
    } else {
      setAppliedProductGroups([]);
    }

    const initialAppliedCountries = getInitialAppliedCountries(
      pagesConfig,
      PageType.CATEGORY_LEADERBOARD,
      effectiveProductType,
    );
    setAppliedCountries(initialAppliedCountries);
    setSelectedCountries(initialAppliedCountries);
  }, [pagesConfig.appliedFilters]);

  /*
   * Updates selectedCountries when selected country is changed and differs from applied country
   * Updating the state will call another useEffect which helps to get the updated list of PGs
   */
  useEffect(() => {
    updateFieldsForOverviewAndCategoryLeaderboardAndExplore(
      pagesConfig,
      selectedCountries,
      setSelectedCountries,
      setSelectedProductGroups,
      updateSideFilters,
    );
  }, [pagesConfig.selectedFilters]);

  let pgsByCountry = pagesConfig.pgsByCountry;
  const clientId = retailerClientState.clientId;
  const filter = retailerOverviewResponseConfig.response[0];

  /*
   * calls API to get the list of product groups to be display in the filter
   */
  useEffect(() => {
    (async () => {
      if (clientId === -1 && !periodicity) {
        return;
      }

      pagesConfig.setSelectedTimeFrame(periodicity);
      const timeFrames = getSubscriptionPeriodicitiesByCountryAndProductType(
        effectiveProductType,
        pagesConfig,
        selectedCountries,
      );

      if (!timeFrames || timeFrames.length === 0) {
        return;
      }
      let pgs = await getProductGroupsByCountry(clientId, selectedCountries, effectiveProductType);

      pgsByCountry = filterPGsByCountry(pgs, selectedCountries);

      updateCountryAndPGFilters(pagesConfig, selectedCountries, pgsByCountry, filter, effectiveProductType);

      if (pagesConfig.masterPgsByCountry.length === 0) {
        let availableMasterPgs = await getProductGroupsMasterByCountry(
          clientId,
          appliedCountries,
          effectiveProductType,
        );
        pagesConfig.setPGsByAppliedCountry(pgsByCountry);
        pagesConfig.setMasterPgsByCountry(availableMasterPgs);
      }
    })();
  }, [pagesConfig.clientCountries, pagesConfig.clientId, selectedCountries, periodicity]);

  /**
   * will trigger this use effect to update the pgs by applied country when new countries and time frame is applied (*not selected)
   */
  useEffect(() => {
    (async () => {
      setPgsByAppliedCountries([]);
      if (
        !checkPGsByCountryPrecondition(
          pagesConfig,
          clientId,
          periodicity,
          sideFiltersState.appliedFilters,
          appliedCountries,
        )
      ) {
        return;
      }
      updatePgsByCountryOnCountryApplyWithProductType(
        pagesConfig,
        clientId,
        selectedCountries,
        appliedCountries,
        effectiveProductType,
        undefined,
        setPgsByAppliedCountries,
      );
      let availableMasterPgs = await getProductGroupsMasterByCountry(clientId, appliedCountries, effectiveProductType);
      pagesConfig.setMasterPgsByCountry(availableMasterPgs);
    })();
  }, [pagesConfig.pgsByCountry]);

  useEffect(() => {
    const payload = {
      selectedGrowthType: selectedGrowthType,
      country: appliedCountries[0].value,
    };
    updateMainFiltersRO(payload);
    const mainFilters = {
      timeFrame: periodicity,
      period: period,
      comparisonPeriod: comparisonPeriod,
      selectedGrowthType: selectedGrowthType,
    };
    //this is to bring the selected timeframe to TA page
    updateMainFiltersMarketPremium({
      ...mainFilters,
    });
    // when timeFrame is changed, whatever that is not applied in filter tray will be reset, selected countries and product groups reset
    removeUnsavedFilters(pagesConfig);
    setSelectedCountries(appliedCountries);
    setSelectedProductGroups(appliedProductGroups);
  }, [period, periodicity, comparisonPeriod]);

  const constructCategoryLeaderboardParam = (): CategoryLeaderboardParam => {
    const appliedFilters = [...pagesConfig.appliedFilters[PageType.CATEGORY_LEADERBOARD].selectedFilters];

    return {
      clientId: retailerClientState.clientId,
      productType: effectiveProductType,
      countries: [appliedCountry()!],
      productGroup: appliedProductGroups,
      period: period.value,
      comparisonPeriod: comparisonPeriod,
      periodType: periodicity,
      shareType: selectedShareType,
      pageRequest: { page: defaultPageNumber, size: defaultPageSize } as PageRequest,
      sortOption: pagesConfig.sortingOptions,
      growthType: selectedGrowthType,
      appliedFilters: appliedFilters,
      groups: allGroupsForApiCalls,
      showPerformance: FilterTitle.CATEGORY,
      dataType: DataType.CATEGORY,
    };
  };

  /*
   * calls leaderboard api and gets it response
   */
  const loadCategoryLeaderboard = async (controller: AbortController) => {
    setIsFetchingTableApi(true);
    setLoadingCategoryLeaderboard(true);
    setIsApiError(false);

    try {
      const loadLeaderboardParam = constructCategoryLeaderboardParam();
      setCategoryLeaderboardParam(loadLeaderboardParam);

      const categoryLeaderboardData: CategoryLeaderboardPgPage = await getCategoryLeaderboard(
        loadLeaderboardParam,
        controller,
      );

      if (categoryLeaderboardData.groupingKpis && categoryLeaderboardData.groupingKpis[0]) {
        setCategoryLeaderboardGroups(categoryLeaderboardData.groupingKpis);
      } else {
        setCategoryLeaderboardGroups([]);
      }

      if (categoryLeaderboardData?.leaderboardKpis && categoryLeaderboardData?.leaderboardKpis.content[0]) {
        setCurrentPage(defaultPageNumber);
        setTotalPage(categoryLeaderboardData.leaderboardKpis.pageable.totalPage);
        setCategoryLeaderboardTotalSize(categoryLeaderboardData.leaderboardKpis.pageable.totalElements);
        setCategoryLeaderboardPg(categoryLeaderboardData.leaderboardKpis.content);
        setCurrentSortingOption(pagesConfig.sortingOptions);
        // exclude TOTAL
        setTotalNumberProductGroups(categoryLeaderboardData.leaderboardKpis.pageable.totalElements - 1);
      } else {
        setCategoryLeaderboardPg([]);
      }
    } catch (err: any) {
      if (axios.isCancel(err)) {
        return;
      }
      saveErrorLog(err, 'Category Leader board - Unable to get leaderboard service.', {
        category: ErrorTagEnum.ApiError,
        requestPayload: {
          clientId: retailerClientState.clientId,
          productGroup: appliedProductGroups,
          period: period.value,
          comparisonPeriod: comparisonPeriod,
        },
      });
      setCategoryLeaderboardPg([]);
      setApiErrorState(err, setIsApiError);
    }
    setLoadingCategoryLeaderboard(false);
    setIsFetchingTableApi(false);
  };

  const loadCategoryLeaderboardGrouping = async (controller: any) => {
    setIsFetchingTableApi(true);
    setIsApiError(false);

    try {
      const loadLeaderboardParam = constructCategoryLeaderboardParam();
      setCategoryLeaderboardParam(loadLeaderboardParam);

      const categoryLeaderboardData: CategoryLeaderboardPgPage = await getCategoryLeaderboard(
        loadLeaderboardParam,
        controller,
      );

      if (categoryLeaderboardData.groupingKpis && categoryLeaderboardData.groupingKpis[0]) {
        setCategoryLeaderboardGroups(categoryLeaderboardData.groupingKpis);
      } else {
        setCategoryLeaderboardGroups([]);
      }
    } catch (err: any) {
      if (axios.isCancel(err)) {
        return;
      }
      saveErrorLog(err, 'Category Leader board - Unable to get leaderboard service.', {
        category: ErrorTagEnum.ApiError,
        requestPayload: {
          clientId: retailerClientState.clientId,
          productGroup: appliedProductGroups,
          period: period.value,
          comparisonPeriod: comparisonPeriod,
        },
      });
      setCategoryLeaderboardPg([]);
      setApiErrorState(err, setIsApiError);
    }
    setIsFetchingTableApi(false);
  };

  /*
   * updates categoryLeaderboardUpdate when comparisonPeriod, sortingOptions, shareType, timeframe, period
   * appliedProductGroups and getCategoryLeaderboardGroups.isLoading are changed
   */
  useEffect(() => {
    const controller = getAbortController();
    const responseTimeFrame = checkPeriodExist(defaultFilters, appliedCountry()!.id, periodicity);
    if (
      retailerClientState.clientId === -1 ||
      !period.value ||
      !comparisonPeriod ||
      !appliedCountry() ||
      !responseTimeFrame ||
      !checkValidComparisonPeriod() ||
      getCategoryLeaderboardGroups.isLoading
    ) {
      setLoadingCategoryLeaderboard(true);
      return;
    }
    if (pagesConfig.sortingOptions !== currentSortingOption) {
      setCategoryLeaderboardPg([]);
    }

    pagesConfig.setIsCountryChanged(false);

    (async () => {
      await loadCategoryLeaderboard(controller);
    })();

    return () => {
      controller.abort();
      setIsFetchingTableApi(false);
      setLoadingCategoryLeaderboard(false);
    };
  }, [
    comparisonPeriod,
    pagesConfig.sortingOptions,
    selectedGrowthType,
    periodicity,
    period,
    appliedProductGroups,
    getCategoryLeaderboardGroups.isLoading,
    clientId,
  ]);

  /**
   * @remarks
   * to call leaderboard API for grouping KPIs when grouping data is changed
   * **/
  useEffect(() => {
    const isTimeFrameExist = checkPeriodExist(defaultFilters, appliedCountry()!.id, periodicity);
    if (!getCategoryLeaderboardGroups.data || !isTimeFrameExist) {
      return;
    }

    setGroupingEffect(false);

    if (groupingEffect) {
      return;
    }

    const controller = getAbortController();

    (async () => loadCategoryLeaderboardGrouping(controller))();

    return () => controller.abort();
  }, [getCategoryLeaderboardGroups.data]);

  const onClick = (isExpanded: boolean) => {
    pagesConfig.toggleFiltersTray(isExpanded);
    resetSideFilter();
  };

  const resetSideFilter = () => {
    pagesConfig.setApplyFilters(false);
    pagesConfig.setClearFilters(false);
  };

  const isLastPage = () => {
    return currentPage === totalPage - 1;
  };

  const handleScroll = async () => {
    if (!checkValidComparisonPeriod() || !isTableView) {
      return;
    }

    if (!loadMoreTable && !isLastPage() && pagesConfig.sortingOptions === currentSortingOption && appliedCountry()) {
      const pageNumber = currentPage + 1;
      setLoadMoreTable(true);
      const appliedFilters = [...pagesConfig.appliedFilters[PageType.CATEGORY_LEADERBOARD].selectedFilters];

      try {
        const loadScrollLeaderboardParam: CategoryLeaderboardParam = {
          clientId: retailerClientState.clientId,
          productType: effectiveProductType,
          periodType: periodicity,
          period: period.value,
          comparisonPeriod: comparisonPeriod,
          growthType: selectedGrowthType,
          shareType: selectedShareType,
          sortOption: pagesConfig.sortingOptions,
          pageRequest: { page: pageNumber, size: defaultPageSize } as PageRequest,
          countries: [appliedCountry()!],
          productGroup: appliedProductGroups,
          appliedFilters: appliedFilters,
        };

        const categoryLeaderboardData = await getCategoryLeaderboard(loadScrollLeaderboardParam, getAbortController());

        setCategoryLeaderboardPg([...categoryLeaderboardPg, ...categoryLeaderboardData.leaderboardKpis.content]);
        setCurrentPage(pageNumber);
        setLoadMoreTable(false);
      } catch (err: any) {
        setLoadMoreTable(false);
        saveErrorLog(err, 'Category Leader board - paginated request error.', {
          category: ErrorTagEnum.ApiError,
          requestPayload: {
            clientId: retailerClientState.clientId,
            productGroup: appliedProductGroups,
            period: period.value,
            comparisonPeriod: comparisonPeriod,
          },
        });
      }
    }
  };

  useEffect(() => {
    const payload = {
      selectedGrowthType: selectedGrowthType,
    };

    updateMainFiltersRO(payload);
  }, [selectedGrowthType]);

  // used to get main filters for the selected countries when country filter is changed
  useEffect(() => {
    if (appliedCountries !== undefined && selectedCountries[0] !== undefined) {
      setGroupingEffect(true);

      const mainFilters = defaultFilters.find((e) => e.country === selectedCountries[0].value)!;
      setFilters(mainFilters);

      const appliedCountryTimePeriodOptions = initialFiltersState.initialFilters.filter(
        byCountry(appliedCountries[0].value),
      )[0].timePeriod;

      const fallbackTimePeriodCombo = getFallbackPeriodCombo(
        { period, periodicity: periodicity, comparisonPeriod: comparisonPeriod },
        supportedPeriodicities,
        appliedCountryTimePeriodOptions,
        customMonthsPeriodState,
      );

      if (fallbackTimePeriodCombo !== null) {
        updatePeriod(fallbackTimePeriodCombo.period);
        updatePeriodicity(fallbackTimePeriodCombo.periodicity);
        updateComparisonPeriod(fallbackTimePeriodCombo.comparisonPeriod);
      }

      getAndUpdatePeriod(
        fallbackTimePeriodCombo ? fallbackTimePeriodCombo.period : period,
        appliedCountryTimePeriodOptions,
        updatePeriod,
      );
    }

    if (!pagesConfig.isTimePeriodFirstLoad) {
      setCountryChanged(true);
    }
    if (!onPageFirstLoad) {
      setIsEditable(true);
    }
  }, [appliedCountries]);

  const [isTableView, setTableView] = useState<boolean>(true);

  //disable export button while the data is still loading
  const [isExportButtonDisabled, setIsExportButtonDisabled] = useState<boolean>(false);
  useEffect(() => {
    if (loadingCategoryLeaderboard) {
      setIsExportButtonDisabled(true);
      return;
    }
    setIsExportButtonDisabled(false);
  }, [loadingCategoryLeaderboard, categoryLeaderboardPg]);

  /**
   * This function is needed because only table allows for retailer MIXES sorting, while the
   * double bar chart only shows retailer SHARES.
   */
  const updateSorting = () => {
    if (selectedShareType === ShareType.MyMix) {
      updateShareTypeAndSortingKeyCL(pagesConfig, setSelectedShareType)(ShareType.MyMix);
    }
  };

  const handleViewChange = (view: ViewType) => {
    setTableView(view === ViewType.Table);
    handleElementClickTracking(view);
    updateSorting();
  };

  const handleElementClickTracking = (title: string) => {
    const params: PageElementClickTrackingParam = {
      eventTitle: PAGE_ELEMENT_CLICK_TRACKING_TITLE,
      sideFilters: sideFiltersState.appliedFilters,
      retailerClientState: retailerClientState,
      track: track,
      pageType: getPageTypeWithPageName(retailerClientState.pageViewing),
      pageName: retailerClientState.pageViewing,
      chartName: CATEGORY_CARD_TITLE,
      optionChosen: title,
      elementName: TOGGLE_CLICKED_TITLE,
      access: pagesConfig.access,
    };
    pageElementClickTrackingFunction(params);
  };

  const renderSegments = () => {
    return (
      <PerformanceSegments
        isLoading={loadingCategoryLeaderboard}
        categoryLeaderboardPg={categoryLeaderboardPg}
        selectedGrowthType={selectedGrowthType}
        setIsExportButtonDisabled={setIsExportButtonDisabled}
        factRevenue={getFactRevenue(pagesConfig, appliedCountries, effectiveProductType)}
        volume={getVolumeBasedOnPeriodType(filters, periodicity)}
        sortingOptions={currentSortingOption}
        setCurrentSortingOption={setCurrentSortingOption}
        onExportReady={onExportReady}
        isApiError={isApiError}
      />
    );
  };

  const renderTable = () => {
    return (
      <>
        <div className="mb-6">
          <TableBoardFilter
            selectedShareType={selectedShareType}
            onChangeSelectedShareType={updateShareTypeAndSortingKeyCL(pagesConfig, setSelectedShareType)}
            chartName={TableName.CATEGORY_LEADERBOARD}
          />
        </div>
        <CategoryGroup
          isLoading={loadingCategoryLeaderboard}
          categoryLeaderboardPg={categoryLeaderboardPg}
          selectedShareType={selectedShareType}
          selectedGrowthType={selectedGrowthType}
          loadMore={loadMoreTable}
          handleScroll={handleScroll}
          countries={{
            dataType: 'COUNTRY',
            feature: 'COUNTRY',
            filterTitle: 'Country',
            filterKey: FilterKeys.COUNTRY,
            options: [
              {
                value: appliedCountry()?.id!,
                label: appliedCountry()?.name!,
              },
            ],
            disabled: false,
          }}
          setIsExportButtonDisabled={setIsExportButtonDisabled}
          factRevenue={getFactRevenue(pagesConfig, appliedCountries, effectiveProductType)}
          categoryLeaderboardGroups={categoryLeaderboardGroups}
          currentSortingOption={currentSortingOption}
          setCurrentSortingOption={setCurrentSortingOption}
          context={context}
          volume={getVolumeBasedOnPeriodType(filters, periodicity)}
          totalName={CURRENT_TOTAL_NAME}
          isExplorePage={false}
          isApiError={isApiError}
        />
      </>
    );
  };

  const renderContent = () => {
    return isTableView ? renderTable() : renderSegments();
  };

  //Export Functionalities
  const [invokeExport, onExportReady] = useChartExport();
  const { download: exportTable } = useExportTable([]);
  const { download: exportDoubleBarChart } = useExportDoubleBarChart();
  const { multipleExportCallbacks } = useExportContext();
  const handleDataExportTrackingFunction = useDataExportTracking('mainFiltersRO');

  const handleExportedTracking = (exportFormats: ExportFormat[]) => {
    const dataExportedTrackingParam: DataExportTrackingElementParam = {
      elementType: isTableView ? ElementType.TABLE : ElementType.BAR_CHART,
      elementKpi: ColumnHeaderDisplayNameForCL(getVolumeBasedOnPeriodType(filters, periodicity))[
        pagesConfig.sortingOptions.headerKey as ColumnHeaderForCL
      ],
      elementSort: pagesConfig.sortingOptions,
      optionChosen: exportFormats.map((format) => getExportTrackingFormat(format)),
    };
    handleDataExportTrackingFunction(dataExportedTrackingParam, dataExportedTrackingFunction);
  };

  /**
   * @return CategoryLeaderboardPg object for grouping export excel
   *
   * @remarks
   * To construct a category leaderboard pg for grouping excel when grouping kpis is empty
   * (meaning inside group got no pgs) and needed to display as blank in Excel file
   * **/
  const constructCategoryLeaderboardPgForGroupingExcel = (): CategoryLeaderboardPg => {
    return {
      segmentOrPg: CURRENT_TOTAL_NAME,
      marketPrice: { value: null!, actual: null!, percentage: null! },
      marketRevenue: { value: null!, actual: null!, percentage: null! },
      marketVolume: { value: null!, actual: null!, percentage: null! },
      yourPrice: { value: null!, actual: null!, percentage: null! },
      yourRevenue: { value: null!, actual: null!, percentage: null! },
      marketExcludeRetailRevenue: { value: null!, actual: null!, percentage: null! },
      marketExcludeRetailVolume: { value: null!, actual: null!, percentage: null! },
      marketExcludeRetailPrice: { value: null!, actual: null!, percentage: null! },
      yourRevenueMix: { growthPercentage: null!, sharePercentage: null! },
      yourRevenueShare: { growthPercentage: null!, sharePercentage: null! },
      yourVolume: { value: null!, actual: null!, percentage: null! },
      yourVolumeMix: { growthPercentage: null!, sharePercentage: null! },
      yourVolumeShare: { growthPercentage: null!, sharePercentage: null! },
    };
  };

  /**
   * @return array of CategoryLeaderboardPg containing TOTAL KPI of pgs and TOTAL KPI of groups
   *
   * @param dataForExport - response data for leaderboard api
   *
   * @remarks
   * to get array of TOTAL for pgs and groups for summary data for export excel so that it becomes sticky row on
   * top of Excel
   * - first to set a default group kpis if the group kpis is empty so that to display kpi value as blank
   *   in the export excel
   * - then filter grouping kpis TOTAL and replace the TOTAL name to group name and assign it to groupsKpis
   *   variable
   * - then map to get all groups TOTAL by filter out product groups KPIs and assign it to groupsTotal variable
   * **/
  const getSummaryDataRowsForExcel = (dataForExport: CategoryLeaderboardPgPage): CategoryLeaderboardPg[] => {
    const groupsWithEmptyKpis: CategoryLeaderboardGroupingPgKpi[] = dataForExport.groupingKpis.filter((group) =>
      group.groupKpis.length === 0
        ? group.groupKpis.push(constructCategoryLeaderboardPgForGroupingExcel())
        : group.groupKpis,
    );

    const groupsKpis: CategoryLeaderboardGroupingPgKpi[] = groupsWithEmptyKpis.filter(
      (group) =>
        (group.groupKpis.find((pgKpis) => pgKpis.segmentOrPg === CURRENT_TOTAL_NAME)!.segmentOrPg =
          group.groupDetails.groupName),
    );

    const groupsTotal: CategoryLeaderboardPg[] = groupsKpis.map(
      (group) =>
        group.groupKpis.find((totalKpis) => !group.groupDetails.segmentOrPgValue.includes(totalKpis.segmentOrPg))!,
    );

    return [...dataForExport.leaderboardKpis.content.splice(0, 1), ...groupsTotal];
  };

  const onChartDownload = useCallback(
    async (exportFormats: ExportFormat[]): Promise<void> => {
      if (!categoryLeaderboardParam || categoryLeaderboardPg.length <= 0) {
        return;
      }
      const timePeriod = period.label;
      const periodType = getPeriodicityForMATAndYTD(period, periodicity).periodicity;
      const comparisonTimePeriod = getComparisonPeriod(period, periodType, comparisonPeriod);

      // Update categoryLeaderboardParam to call the API again with totalElements received from last call
      const requestPayload: CategoryLeaderboardParam = categoryLeaderboardParam;
      requestPayload.pageRequest.size = categoryLeaderboardTotalSize;

      try {
        const controller = getAbortController();
        const dataForExport: CategoryLeaderboardPgPage = await getCategoryLeaderboard(requestPayload, controller);

        const summaryData: CategoryLeaderboardPg[] = modifiedCLSegmentsGrandTotalToTotalValue(
          getSummaryDataRowsForExcel(dataForExport),
        );

        const data: CategoryLeaderboardPg[] = modifiedCLSegmentsGrandTotalToTotalValue(
          dataForExport.leaderboardKpis.content,
        );

        const selectedKpiOption: Option = {
          label: getKpiDisplayNameFromHeaderKey(
            pagesConfig.sortingOptions.headerKey,
            getVolumeBasedOnPeriodType(filters, periodicity),
          ),
          value: pagesConfig.sortingOptions.headerKey,
        };
        const factRevenue: FactRevenue = getFactRevenue(pagesConfig, appliedCountries, effectiveProductType);
        const appliedFiltersText: string = getAppliedFiltersText(pagesConfig, PageType.CATEGORY_LEADERBOARD);
        const volume: string = getVolumeBasedOnPeriodType(filters, periodicity);

        if (isTableView) {
          await exportTable({
            exportFormats,
            invokeExport,
            title: CATEGORY_CARD_TITLE,
            appliedCountry: appliedCountries,
            appliedPg: appliedProductGroups,
            data,
            summaryData,
            timePeriod,
            comparisonTimePeriod,
            factRevenue: factRevenue,
            periodType,
            showPerformanceWithin: '',
            appliedFiltersText: appliedFiltersText,
            selectedKpi: selectedKpiOption,
            pageName: PageName.CATEGORY_LEADERBOARD,
            pagesConfig: pagesConfig,
            volume: volume,
          });
        } else {
          const options: ExportDoubleBarChartOptions = {
            exportFormats: exportFormats,
            invokeExport: invokeExport,
            title: CATEGORY_CARD_TITLE,
            appliedCountry: appliedCountries,
            appliedProductGroup: appliedProductGroups,
            data: data,
            period: timePeriod,
            comparisonTimePeriod,
            factRevenue: factRevenue,
            periodType: periodType,
            appliedFiltersText: appliedFiltersText,
            selectedKpi: selectedKpiOption,
            multipleChartsExport: multipleExportCallbacks,
            pageName: PageName.CATEGORY_LEADERBOARD,
            volume: volume,
          };
          await exportDoubleBarChart(options);
        }

        handleExportedTracking(exportFormats);
      } catch (err: any) {
        displayExportErrorToastMessage(
          err,
          'Category Leaderboard - exportTable error.',
          requestPayload,
          showToastMessage,
        );
        return;
      }
    },
    [
      categoryLeaderboardParam,
      isTableView,
      categoryLeaderboardPg,
      categoryLeaderboardTotalSize,
      multipleExportCallbacks,
      exportTable,
      exportDoubleBarChart,
    ],
  );

  useSideMenuFilters({
    clientId: pagesConfig.clientId,
    timeFrame: periodicity,
    selectedCountries: selectedCountries,
    selectedProducts: selectedProductGroups,
    appliedProductGroups,
    responseConfig: retailerOverviewResponseConfig,
    pagesConfig: pagesConfig,
    initialFilters: initialFiltersState.initialFilters,
    currentTotalName: CURRENT_TOTAL_NAME,
    productType: effectiveProductType,
  });

  /**
   * To handle the change event when select other retailer in the retailer dropdown - reset the necessary states in order to refresh the reports
   * @param subscriptions client subscription from opa policy
   */
  const onChangeHandler = async (subscriptions: ClientSubscription[]) => {
    setLoadingOverlay(true);
    /**
     * @remark
     * Related to grouping to update state
     * groupingEffect - to prevent the useEffect for grouping leaderboard API from being triggered
     * when multibanner changed
     * **/
    setGroupingEffect(true);

    const subscribedCountries = mapSubscribedCountries(subscriptions);

    // Get the current side menu filters selection from pagesConfig
    const pagesConfigAllFilters = [...pagesConfig.appliedFilters];

    // Default clientId, but the clientId should be always the same no matter how many countries the client has
    const defaultClientId = subscriptions[0].retailer;

    // get hitlists subscription. current assumption is that it exist
    const hitlistsSubscriptions = getSubscriptionForOtherProducts(
      ProductType.ProductAnalyzer,
      pagesConfig,
      defaultClientId,
    );
    const hitlistsSubscribedCountries = mapSubscribedCountries(hitlistsSubscriptions);

    // Default country, retain the countries selection if exists in the subscription
    const defaultCountry = mapDefaultSubscribedCountry(subscriptions, appliedCountries);
    const defaultCountryCode = defaultCountry.countryCode;

    //Retrieve client name
    const selectedClientName = getSelectedClientNameWithClientId(retailerClients, defaultClientId);

    if (hitlistsSubscribedCountries.length !== 0) {
      const onChangeCLMethodParam: OnRetailerChangeParam = {
        subscriptions: hitlistsSubscriptions,
        appliedCountries,
        defaultClientId,
        pagesConfig,
        productType: ProductType.ProductAnalyzer,
        productDataType: ProductDataType.ProductAnalyzer,
        subscribedCountries: hitlistsSubscribedCountries,
        setLoadingOverlay: (_) => {},
        timeFrame: periodicity,
        pagesConfigAllFilters,
        pageName: PageName.CATEGORY_LEADERBOARD,
        pageList: [PageType.RI_PRODUCT],
      };

      await onChangeHandlerForOtherProducts(onChangeCLMethodParam).then((response) => {
        if (!response) {
          return;
        }
        // if update to react 18 and above, can remove call to batch function
        batch(() => {
          updateHitlistsRetailerClient(
            constructRetailerClientState(defaultClientId, hitlistsSubscribedCountries, selectedClientName),
          );
          updateInitialFiltersProducts({ initialFilters: response.filtersData });
          updateMainFiltersProducts({
            timeFrame: response.currTimeFrame,
            period: response.currPeriod,
            comparisonPeriod: RelativePeriod.PreviousYearCurrentPeriod,
            selectedGrowthType: mainFilterStateProducts.selectedGrowthType,
            country: response.defaultCountryCode,
          });
        });
      });
      constructCountryOptionForSideMenu(pagesConfigAllFilters, defaultCountry, [PageType.RI_PRODUCT]);
    }

    // Display saved filter according to selected retailer for both product types
    getAndStoreSavedFilters(effectiveProductType, defaultClientId, pagesConfig, pagesConfig.userId);

    const filtersData = await getFiltersForClient(
      defaultClientId,
      subscribedCountries,
      (_) => {},
      effectiveProductType,
      ProductDataType.PerformanceAnalyzer,
    );

    const filtersDataBasedOnCountry = filtersData.find((f) => f.country === defaultCountryCode);

    setDefaultFilters(filtersData);

    constructCountryOptionForSideMenu(pagesConfigAllFilters, defaultCountry, [
      PageType.RETAILER_OVERVIEW,
      PageType.CATEGORY_LEADERBOARD,
      PageType.OPPORTUNITY_REVIEW,
      PageType.PERFORMANCE_ANALYSIS,
    ]);

    if (!filtersDataBasedOnCountry) {
      setLoadingOverlay(false);
      saveErrorLogForOnChange(pagesConfig.clientCountries, pagesConfig.appliedFilters, PageName.CATEGORY_LEADERBOARD);
      return;
    }

    const currTimeFrame = retainTimeFrame(filtersDataBasedOnCountry, periodicity);

    // Construct state to update Redux Store
    const payload = {
      selectedGrowthType,
      defaultCountryCode,
    };
    const storeFilterSettings = {
      appliedFilters: pagesConfigAllFilters,
    };

    const marketPremiumSubscription = getSubscriptionForOtherProducts(
      ProductType.MarketPremium,
      pagesConfig,
      defaultClientId,
    );
    const marketPremiumSubscribedCountries = mapSubscribedCountries(marketPremiumSubscription);
    resetSideFilter();

    /**
     * The reset sequence below is very important, this is due to the massive useEffect that we have in the page
     */
    // if update to react 18 and above, can remove call to batch function
    batch(async () => {
      updateSideFilters(storeFilterSettings);
      updateMainFiltersRO(payload);
      updateRetailerClient(
        constructRetailerClientState(
          defaultClientId,
          subscribedCountries,
          selectedClientName,
          PageName.CATEGORY_LEADERBOARD,
        ),
      );
      updateInitialFilters({ initialFilters: filtersData });
      if (marketPremiumSubscribedCountries.length > 0) {
        updateInitialFiltersMarketPremium({ initialFilters: filtersData });
        updateMarketPremiumRetailerClient(
          constructRetailerClientState(
            defaultClientId,
            subscribedCountries,
            selectedClientName,
            PageName.CATEGORY_LEADERBOARD,
          ),
        );
        pagesConfig.setMarketPremiumClientId(defaultClientId);
        pagesConfig.setMarketPremiumClientCountries(subscribedCountries);
      }

      // Do not simply move the setState below, it will trigger unintended side effects
      pagesConfig.setSelectedFilters(pagesConfigAllFilters);

      setSelectedCountries(constructCountryOption(defaultCountry));

      // This is to update the selected feature in OR if the user changed to another retailer in Overview
      const productGroup = getProductGroupFromSideFilters(sideFiltersState.appliedFilters[PageType.OPPORTUNITY_REVIEW]);

      pagesConfig.setClientCountries(subscribedCountries);
      pagesConfig.setClientId(defaultClientId);
      if (hitlistsSubscriptions.length !== 0) {
        pagesConfig.setHitlistsClientCountries(hitlistsSubscribedCountries);
        pagesConfig.setHitlistsClientId(defaultClientId);
      }

      setFilters(filtersDataBasedOnCountry);
      setAppliedCountries(constructCountryOption(defaultCountry));

      // Only update the selected feature in OR if the user has navigated to the page before
      if (productGroup) {
        const featureValues = await getFeatureValueForRetailer({
          clientId: defaultClientId,
          productType: effectiveProductType,
          productDataType: ProductDataType.PerformanceAnalyzer,
          selectedCountries: constructCountryOption(defaultCountry),
          timeFrame: currTimeFrame,
          selectedProductGroup: productGroup.options[0].value,
          currentTotalName: CURRENT_TOTAL_NAME,
        } as RetailerDetails);

        updateMainFilters({
          feature: getFeatureSelector(featureValues, mainFiltersState.feature),
        });
      }
    });
    await refetch();
    setLoadingOverlay(false);
  };

  const getSaveViewFilters = () => {
    const handledTimeFrame = getPeriodicityForMATAndYTD(period, periodicity);
    const categoryLeaderbordViewState: CategoryLeaderboardViewState = {
      periodicity: handledTimeFrame.periodicity,
      matYtdTimePeriodLabel: handledTimeFrame.matYtdLabel,
      comparisonPeriod: isBaseTimePeriodFormat(comparisonPeriod)
        ? RelativePeriodLabel.PreviousPeriod
        : RelativePeriodMapping[comparisonPeriod as RelativePeriod],
      growthType: selectedGrowthType.charAt(0).toUpperCase() + selectedGrowthType.slice(1),
      appliedFilters: pagesConfig.appliedFilters[PageType.CATEGORY_LEADERBOARD].selectedFilters,
      retailerClientState: retailerClientState,
      initialFilterState: initialFiltersState,
      appliedCountries: appliedCountries,
      hitlistRetailerClientState: hitlistsRetailerClientState,
      hitlistInitialFilterState: initialFiltersProductsState,
      isTableView: isTableView,
      selectedShareType: selectedShareType,
      sorting: currentSortingOption,
      productMainFilterState: mainFilterStateProducts,
    };
    return categoryLeaderbordViewState;
  };

  const applyTimeFramePickerCallback: (
    appliedPeriodicity: Periodicity,
    appliedPeriod: BaseTimePeriod,
    appliedRelativePeriod: RelativePeriod | BaseTimePeriod,
  ) => void = (appliedPeriodicity, appliedPeriod, appliedRelativePeriod) => {
    applyTimeFramePicker({
      givenPeriodicity: appliedPeriodicity,
      givenPeriod: appliedPeriod,
      givenComparison: appliedRelativePeriod,
      pageType: getPageTypeWithPageName(retailerClientState.pageViewing),
      pageName: retailerClientState.pageViewing,
      pagesConfig: pagesConfig,
      countryChanged: countryChanged,
      sideFiltersState: sideFiltersState,
      retailerClientState: retailerClientState,
      track: track,
      setTimeFrame: updatePeriodicity,
      setPeriod: updatePeriod,
      setRelativePeriod: updateComparisonPeriod,
      setCountryChanged: setCountryChanged,
      setCustomMonthPeriodState: updateCustomMonthsPeriodState,
    });
  };

  useFetchSalesPerformance({
    productType: effectiveProductType,
    timeFrame: periodicity,
    period,
    comparisonPeriod,
    defaultFilters,
    clientId: retailerClientState.clientId,
    setIsFetchingCardApiCallback(isFetchingApi: boolean) {
      setIsFetchingCardApi(isFetchingApi);
    },
    setLoadingPosFacts,
    setVolumePerformance,
    setRevenuePerformance,
    pageType: PageType.CATEGORY_LEADERBOARD,
    setIsApiErrorCallback: setIsApiError,
  });

  const filteredScopeData = getCountryFilterPairsByTimeFrame(pagesConfig.pgsByCountry, periodicity);

  const getSaveViewTrackingObject = () => {
    return {
      access: pagesConfig.access,
      locationId: appliedCountries.map((country) => country.label),
      pageName: PageName.CATEGORY_LEADERBOARD,
      itemGroup:
        appliedProductGroups.map((pg) => pg.label).length === 0
          ? 'ALL'
          : appliedProductGroups.map((pg) => pg.label).toString(),
      section: TrackingGeneralProp.SECTION_TRACKING_PROP,
      subSection: PageName.CATEGORY_LEADERBOARD,
    };
  };

  /**
   * Creates insight data for double bar chart.
   * @param collectionDataType Specifies whether to use live data or not. Default is 'live'.
   * @returns Insight data for double bar chart.
   */
  const createDoubleBarChartInsightDataFn: CreateInsightDataFn<CategoryLeaderboardDoubleBarChartInsight> = (
    collectionDataType: CollectionDataType = 'live',
  ) => {
    const volume = getVolumeBasedOnPeriodType(filters, periodicity);

    const kpiSelectionOptions = getKPIs(getCategoryLeaderboardColumns(ShareType.MarketShare, volume));
    const selectedKpi =
      kpiSelectionOptions.find((option) => option.value === pagesConfig.sortingOptions.headerKey) ??
      kpiSelectionOptions[0];

    const params = constructCategoryLeaderboardParam();

    const { productGroup, appliedFilters } = filterByAccessibleProductGroup(
      params.appliedFilters,
      pagesConfig.pgsByAppliedCountry,
      params.periodType,
      period.value,
      appliedCountries,
      appliedProductGroups,
    );

    return {
      apiParams: {
        clientId: params.clientId,
        productType: params.productType,
        periodType: params.periodType,
        period: params.period,
        comparisonPeriod: params.comparisonPeriod,
        growthType: params.growthType,
        shareType: params.shareType,
        sortOption: params.sortOption,
        pageRequest: params.pageRequest,
        countries: params.countries,
        productGroup: productGroup,
        appliedFilters: appliedFilters,
      },
      selectedGrowthType: selectedGrowthType,
      factRevenue: getFactRevenue(pagesConfig, appliedCountries, effectiveProductType),
      volume: volume,
      selectedKpiOption: selectedKpi,
      collectionDataType: collectionDataType,
      retailerClientName: retailerClientState.clientName,
    };
  };

  const { Card } = containers;
  return (
    <div>
      <Page>
        <Page.Title>
          <PageHeader
            retailerClients={retailerClients}
            onChangeHandler={onChangeHandler}
            productType={effectiveProductType}
          >
            <div className={styles.Summary__GridContainer}>
              <h1 className={styles.Summary__Title}>
                <span>Category Leaderboard</span>
                <PageInfo
                  title={'Category Leaderboard'}
                  content={
                    <section>
                      <p>
                        Starting point for an instant summary - See the complete picture of your performance across the
                        whole market.
                      </p>
                      <p>
                        Review all your categories in one place, with topline results, growths and shares for you and
                        the market, as a starting point for a deeper product group evaluation. Toggle between Chart and
                        Table views, and customise reporting with Filters for countries and product groups. Spot
                        something that needs a closer look? Click through from the table to move to a deeper analysis.
                      </p>
                      <p>
                        Subscribed to a market&apos;s weekly or monthly data? Select the market to move to a deeper
                        analysis.
                      </p>
                    </section>
                  }
                />
              </h1>
              {isAuthorizedUser && (
                <div className={styles.Summary__SavingViews}>
                  <CategoryLeaderboardCopyViewButton
                    categoryLeaderboardViewState={getSaveViewFilters()}
                    copyViewTracking={getSaveViewTrackingObject()}
                  />
                  <FilterToggleButton onClick={onClick} testId="filter-button" />
                </div>
              )}
            </div>
          </PageHeader>
        </Page.Title>
        <Page.Content>
          <InfoModal />
          {isAuthorizedUser ? (
            <>
              <LoadingOverlay loadingOverlay={loadingOverlay} />
              <div className="flex items-end mt-4">
                <div className={`${styles.Summary__Controls} w-full grid mt-4`}>
                  <TimeFramePickerWrapper
                    isPeriodOptionsDisabled={false}
                    isTimeFramePickerDisabled={isFetchingTableApi || isFetchingCardApi}
                    currentPeriod={period}
                    currentPeriodicity={periodicity}
                    currentComparison={comparisonPeriod}
                    periodOptions={filters?.timePeriod}
                    applyPickerCallback={applyTimeFramePickerCallback}
                    customMonthsPeriod={customMonthsPeriodState}
                    appliedCountries={appliedCountries}
                    appliedProductGroups={appliedProductGroups}
                  />
                  <ComparisonGrowthSelector
                    growthOptions={filters?.comparisonGrowth}
                    growthOption={(growth: GrowthTypes) => setGrowthOption(growth)}
                    selectedGrowthOption={selectedGrowthType}
                    isDisabled={false}
                  />
                </div>
              </div>
              <br />
              <FilterSelectionNote
                selectedPeriod={period}
                comparisonPeriodType={comparisonPeriod}
                selectedPeriodicity={periodicity}
                scopeMetadata={filteredScopeData[0]?.scopeMetadata}
              />
              <br />
              {<PgAvailabilityBanner />}
              <WarningProductGroupFilterContainer pagesConfig={pagesConfig} />
              <FilterChips pgListByCountry={pagesConfig.pgsByAppliedCountry} selectedTimeFrame={periodicity} />
              {!onPageFirstLoad && (
                <Card>
                  <MarketPerformancePosFacts
                    revenuePerformance={revenuePerformance}
                    volumePerformance={volumePerformance}
                    selectedGrowthType={selectedGrowthType}
                    isLoading={loadingPosFacts}
                    factRevenue={getFactRevenue(pagesConfig, appliedCountries, effectiveProductType)}
                    volume={getVolumeBasedOnPeriodType(filters, periodicity)}
                    pageName={PageName.CATEGORY_LEADERBOARD}
                    isError={isApiError}
                  />
                </Card>
              )}
              <br />
              <CategoryOrSegmentsLeaderboardGroupProvider>
                <Card testId={CATEGORY_CARD_TITLE}>
                  <Card.Title title={CATEGORY_CARD_TITLE}>
                    <Card.Title.Actions>
                      <CategoryLeaderboardHeaderIcon
                        isTableView={isTableView}
                        onChartDownload={onChartDownload}
                        isExportButtonDisabled={isExportButtonDisabled}
                        volume={getVolumeBasedOnPeriodType(filters, periodicity)}
                        createInsightDataFn={createDoubleBarChartInsightDataFn}
                        onChange={handleViewChange}
                      />
                    </Card.Title.Actions>
                  </Card.Title>
                  {renderContent()}
                </Card>
                {!loadingCategoryLeaderboard && isTableView && (
                  <>
                    <CategoryLeaderboardActionBar
                      totalProductGroups={totalNumberProductGroups}
                      context={context}
                      isEditable={isEditable}
                      setIsEditable={setIsEditable}
                    />
                    <CreateCategoryOrSegmentsLeaderboardModalGroup context={context} sortOrder={getNewSortValues} />
                    <DeleteCategoryOrSegmentsLeaderboardModalGroup context={context} />
                  </>
                )}
              </CategoryOrSegmentsLeaderboardGroupProvider>
            </>
          ) : (
            Unauthorized()
          )}
        </Page.Content>
        <Page.Footer> </Page.Footer>
      </Page>
    </div>
  );
};

export default CategoryLeaderboard;
